import { ContentEdit } from '@fs/zion-icon'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  Select,
  TextField,
  Row,
} from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { updatePerson } from '../../../../services/famousHistoricalService'
import DmcDataView from '../../../common/DmcDataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'
import FamousDeletePersonButton from './FamousDeletePersonButton'

export default function FamousPersonDataDialog({
  person,
  removePersonOnDelete,
  updatePersonOnEdit,
  countries,
  ...personDataOverlay
}) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const personJSON = JSON.parse(person.personJSON)
  const ogPersonData = {
    ...personJSON,
    birthDateItem: { key: 'birthDateItem', primaryText: personJSON.birthDate, formattedDate: personJSON.birthDate },
    deathDateItem: { key: 'deathDateItem', primaryText: personJSON.deathDate, formattedDate: personJSON.deathDate },
  }
  const [personData, setPersonData] = useState(ogPersonData)
  const ogCountry = person.country
  const [country, setCountry] = useState(person.country)
  const [editing, setEditing] = useState(false)
  const ogPersonFullName = `${ogPersonData.firstName} ${ogPersonData.surname}`
  const personFullName = `${personData.firstName} ${personData.surname}`
  const data = getData()

  function shouldDisableSaveButton() {
    return (
      JSON.stringify({ ...ogPersonData, birthDateItem: undefined, deathDateItem: undefined }) ===
        JSON.stringify({ ...personData, birthDateItem: undefined, deathDateItem: undefined }) &&
      (ogPersonData.birthDate === personData.birthDateItem ||
        ogPersonData.birthDate === personData.birthDateItem?.formattedDate) &&
      ogPersonData.deathDate === personData.deathDateItem?.formattedDate &&
      country === person.country
    )
  }

  useEffect(() => {
    setCountry(person.country)
  }, [person])

  useEffect(() => {
    setPersonData({
      ...JSON.parse(person.personJSON),
      birthDateItem: {
        key: 'birthDateItem',
        primaryText: ogPersonData.birthDate,
        formattedDate: ogPersonData.birthDate,
      },
      deathDateItem: {
        key: 'deathDateItem',
        primaryText: ogPersonData.deathDate,
        formattedDate: ogPersonData.deathDate,
      },
    })
    setEditing(false)
  }, [ogPersonData.birthDate, ogPersonData.deathDate, person])

  const onPersonChange = (fieldType, newValue) => {
    setPersonData({
      ...personData,
      [fieldType]: newValue,
    })
  }

  const doneBtnOnClick = () => {
    personDataOverlay.close()
    setPersonData({
      ...ogPersonData,
      birthDateItem: {
        key: 'birthDateItem',
        primaryText: ogPersonData.birthDate,
        formattedDate: ogPersonData.birthDate,
      },
      deathDateItem: {
        key: 'deathDateItem',
        primaryText: ogPersonData.deathDate,
        formattedDate: ogPersonData.deathDate,
      },
    })
  }

  const cancelBtnOnClick = () => {
    setPersonData({
      ...ogPersonData,
      birthDateItem: {
        key: 'birthDateItem',
        primaryText: ogPersonData.birthDate,
        formattedDate: ogPersonData.birthDate,
      },
      deathDateItem: {
        key: 'deathDateItem',
        primaryText: ogPersonData.deathDate,
        formattedDate: ogPersonData.deathDate,
      },
    })
    setCountry(ogCountry)
    setEditing(!editing)
  }

  const saveBtnOnClick = async () => {
    const personDataWithFormattedDates = {
      ...personData,
      birthDate: personData.birthDateItem && personData.birthDateItem.formattedDate,
      deathDate: personData.deathDateItem && personData.deathDateItem.formattedDate,
    }
    delete personDataWithFormattedDates.birthDateItem
    delete personDataWithFormattedDates.deathDateItem

    const personDto = {
      id: person.id,
      metaDataId: person.metaDataId,
      personJSON: JSON.stringify(personDataWithFormattedDates),
      matchType: person.matchType,
      country,
    }
    try {
      await updatePerson(personDto)
      updatePersonOnEdit(person.id, personDto.personJSON, country)
      setEditing(false)
    } catch (err) {
      error(
        t('tools.errors.editingFamousPerson', {
          defaultValue: `Error editing famous person: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  function getData() {
    if (editing) {
      return getFamousDataInEditMode()
    }
    return getFamousDataInViewMode()
  }

  function getFamousDataInViewMode() {
    return [
      {
        label: <b>{t('tools.lists.labels.firstName', 'First Name')}</b>,
        detail: personData.firstName,
        key: 'firstName',
      },
      {
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: personData.surname,
        key: 'surname',
      },
      {
        label: <b>{t('category', 'Category')}</b>,
        detail: personData.category,
        key: 'category',
      },
      {
        label: <b>{t('tools.lists.labels.birthDate', 'Birth Date')}</b>,
        detail: personData.birthDateItem.formattedDate,
        key: 'birthDateItem',
      },
      {
        label: <b>{t('birthPlace', 'Birth Place')}</b>,
        detail: personData?.birthPlace?.replace(/"/g, ''),
        key: 'birthPlace',
      },
      {
        label: <b>{t('gender.label', 'Gender')}</b>,
        detail: personData.gender,
        key: 'gender',
      },
      {
        label: <b>{t('deathDate', 'Death Date')}</b>,
        detail: personData.deathDateItem.formattedDate,
        key: 'deathDateItem',
      },
      {
        label: <b>{t('deathPlace', 'Death Place')}</b>,
        detail: personData?.deathPlace?.replace(/"/g, ''),
        key: 'deathPlace',
      },
      {
        label: <b>{t('biography', 'Biography')}</b>,
        detail: personData.biography,
        key: 'biography',
      },
      {
        label: <b>{t('tools.lists.labels.parent1GivenName', 'Parent #1 Given Name')}</b>,
        detail: personData.parent1GivenName,
        key: 'parent1GivenName',
      },
      {
        label: <b>{t('tools.lists.labels.parent1Surname', 'Parent #1 Surname')}</b>,
        detail: personData.parent1Surname,
        key: 'parent1Surname',
      },
      {
        label: null,
        detail: null,
        key: 'spacer1',
      },
      {
        label: <b>{t('tools.lists.labels.parent2GivenName', 'Parent #2 Given Name')}</b>,
        detail: personData.parent2GivenName,
        key: 'parent2GivenName',
      },
      {
        label: <b>{t('tools.lists.labels.parent2Surname', 'Parent #2 Surname')}</b>,
        detail: personData.parent2Surname,
        key: 'parent2Surname',
      },
      {
        label: null,
        detail: null,
        key: 'spacer2',
      },
      {
        label: <b>{t('tools.lists.labels.spouseGivenName', 'Spouse Given Name')}</b>,
        detail: personData.spouseGivenName,
        key: 'spouseGivenName',
      },
      {
        label: <b>{t('tools.lists.labels.spouseSurname', 'Spouse Surname')}</b>,
        detail: personData.spouseSurname,
        key: 'spouseSurname',
      },
    ]
  }

  function getFamousDataInEditMode() {
    return [
      {
        key: 'famousFirstName',
        label: <b>{t('tools.lists.labels.firstName', 'First Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.firstName}
            onChange={(e) => onPersonChange('firstName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousSurname',
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.surname}
            onChange={(e) => onPersonChange('surname', e.target.value)}
          />
        ),
      },
      {
        key: 'famousCategory',
        label: <b>{t('category', 'Category')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectCategory', 'Select Category')}
            value={personData.category}
            onChange={(e) => onPersonChange('category', e.target.value)}
            options={[
              {
                value: 'BIB',
                label: t('famous.category.biblical', 'Biblical'),
              },
              {
                value: 'ENT',
                label: t('famous.category.entertainment', 'Entertainment'),
              },
              {
                value: 'FIC',
                label: t('famous.category.fictional', 'fictional'),
              },
              {
                value: 'HIS',
                label: t('famous.category.historical', 'Historical'),
              },
              {
                value: 'HOL',
                label: t('famous.category.holocaust', 'Holocaust'),
              },
              {
                value: 'MIL',
                label: t('famous.category.military', 'Military'),
              },
              {
                value: 'OTH',
                label: t('famous.category.other', 'Other'),
              },
              {
                value: 'POL',
                label: t('famous.category.political', 'Political'),
              },
              {
                value: 'REL',
                label: t('famous.category.religious', 'Religious'),
              },
              {
                value: 'SPO',
                label: t('famous.category.sports', 'Sports'),
              },
              {
                value: 'TER',
                label: t('famous.category.terrorist', 'Terrorist'),
              },
              {
                value: 'TRG',
                label: t('famous.category.tragedy', 'Tragedy'),
              },
            ]}
          />
        ),
      },
      {
        key: 'famousBirthDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('tools.lists.labels.birthDate', 'Birth Date')}
            selectedItem={personData.birthDateItem}
            setSelectedItem={(e) => onPersonChange('birthDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      {
        key: 'famousBirthPlace',
        label: <b>{t('birthPlace', 'Birth Place')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData?.birthPlace?.replace(/"/g, '')}
            onChange={(e) => onPersonChange('birthPlace', e.target.value)}
          />
        ),
      },
      {
        key: 'famousGender',
        label: <b>{t('gender.label', 'Gender')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectGender', 'Select Gender')}
            value={personData.gender}
            onChange={(e) => onPersonChange('gender', e.target.value)}
            options={[
              { value: 'Male', label: t('gender.male', 'Male') },
              { value: 'Female', label: t('gender.female', 'Female') },
            ]}
          />
        ),
      },
      {
        key: 'famousDeathDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('deathDate', 'Death Date')}
            selectedItem={personData.deathDateItem}
            setSelectedItem={(e) => onPersonChange('deathDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      {
        key: 'famousDeathPlace',
        label: <b>{t('deathPlace', 'Death Place')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData?.deathPlace?.replace(/"/g, '')}
            onChange={(e) => onPersonChange('deathPlace', e.target.value)}
          />
        ),
      },
      {
        key: 'famousBiography',
        label: <b>{t('biography', 'Biography')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.biography}
            onChange={(e) => onPersonChange('biography', e.target.value)}
          />
        ),
      },
      {
        key: 'famousParent1GivenName',
        label: <b>{t('tools.lists.labels.parent1GivenName', 'Parent #1 Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.parent1GivenName}
            onChange={(e) => onPersonChange('parent1GivenName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousParent1Surname',
        label: <b>{t('tools.lists.labels.parent1Surname', 'Parent #1 Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.parent1Surname}
            onChange={(e) => onPersonChange('parent1Surname', e.target.value)}
          />
        ),
      },
      {
        label: null,
        detail: null,
        key: 'spacer1',
      },
      {
        key: 'famousParent2GivenName',
        label: <b>{t('tools.lists.labels.parent2GivenName', 'Parent #2 Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.parent2GivenName}
            onChange={(e) => onPersonChange('parent2GivenName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousParent2Surname',
        label: <b>{t('tools.lists.labels.parent2Surname', 'Parent #2 Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.parent2Surname}
            onChange={(e) => onPersonChange('parent2Surname', e.target.value)}
          />
        ),
      },
      {
        label: null,
        detail: null,
        key: 'spacer2',
      },
      {
        key: 'famousSpouseGivenName',
        label: <b>{t('tools.lists.labels.spouseGivenName', 'Spouse Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.spouseGivenName}
            onChange={(e) => onPersonChange('spouseGivenName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousSpouseSurname',
        label: <b>{t('tools.lists.labels.spouseSurname', 'Spouse Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.spouseSurname}
            onChange={(e) => onPersonChange('spouseSurname', e.target.value)}
          />
        ),
      },
    ]
  }

  return (
    <DialogOverlay
      {...personDataOverlay}
      title={editing ? ogPersonFullName : personFullName}
      subtitle={!editing && country.replace('_', ' ')}
      size="xxl"
    >
      {personData && (
        <>
          {editing && (
            <Grid>
              <Cell columns={3}>
                <Select
                  id="country"
                  placeholder={t('tools.lists.placeholders.selectMultiAreaTeam', 'Select Multi-area Team')}
                  label={t('tools.lists.labels.multiAreaTeam', 'Multi-area Team')}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  options={countries}
                />
              </Cell>
              <p columns={9} />
            </Grid>
          )}
          <DmcDataView data={data} />
        </>
      )}

      <DialogOverlayFooter>
        <DialogOverlayActions>
          {editing && (
            <Row>
              <Button emphasis="high" onClick={saveBtnOnClick} disabled={shouldDisableSaveButton()}>
                {t('action.save', 'Save')}
              </Button>
              <Button emphasis="medium" onClick={cancelBtnOnClick}>
                {t('action.cancel', 'Cancel')}
              </Button>
            </Row>
          )}
          {!editing && (
            <Row>
              <Button emphasis="medium" Icon={ContentEdit} onClick={() => setEditing(!editing)}>
                {t('action.edit', 'Edit')}
              </Button>
              <FamousDeletePersonButton
                person={person}
                personFullName={ogPersonFullName}
                removePersonOnDelete={removePersonOnDelete}
                {...personDataOverlay}
              />
              <Button emphasis="medium" onClick={doneBtnOnClick}>
                {t('action.done', 'Done')}
              </Button>
            </Row>
          )}
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
